// src/components/common/Navigation.js
import React, { useState, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Languages, Menu, X } from 'lucide-react';
import { LanguageContext } from '../../contexts/LanguageContext';

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { lang, setLang } = useContext(LanguageContext);
  const location = useLocation();

  const translations = {
    ja: {
      about: 'サービスについて',
      faq: 'よくある質問', 
      contact: 'お問い合わせ',
      download: 'お役立ち資料ダウンロード',
      menuToggle: 'メニューを切り替え',
      language: '言語',
      siteName: '中小企業のための採用診断サイト'
    },
    en: {
      about: 'About Service',
      faq: 'FAQ',
      contact: 'Contact',
      download: 'Resource Download',
      menuToggle: 'Toggle menu',
      language: 'Language',
      siteName: 'SME Recruitment Assessment'
    }
  };

  const t = translations[lang] || translations.ja;

  const navItems = [
    { path: '/about', label: t.about, key: 'about' },
    { path: '/faq', label: t.faq, key: 'faq' },
    { path: '/contact', label: t.contact, key: 'contact' },
  ];

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const isActivePath = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  if (!setLang) {
    console.warn("LanguageContext's setLang is not available.");
    return null;
  }

  return (
    <>
      <nav className="bg-white shadow-sm fixed top-0 left-0 right-0 z-50">
        <div className="px-4 2xl:px-8">
          <div className="flex justify-between items-center h-16 2xl:h-24">
            {/* Logo/Brand */}
            {/* <div className="flex-shrink-0">
              <Link 
                to="/" 
                className="text-xl font-bold text-gray-800 hover:text-blue-600 transition-colors duration-200"
                onClick={closeMenu}
              >
                <img
                  src="/web_logo.png"
                  alt={t.siteName}
                  className="h-10 w-auto 2xl:h-16"
                  style={{ maxHeight: 40 }}
                />
              </Link>
            </div> */}

            {/* Desktop Navigation - right aligned */}
            <div className="hidden md:flex flex-1 justify-end items-center space-x-4 2xl:space-x-8">
              {navItems.map((item) => {
                if (item.key === 'contact') {
                  return (
                    <Link
                      key={item.key}
                      to={item.path}
                      className="text-white px-4 py-2 2xl:px-8 2xl:py-4 rounded-full text-sm 2xl:text-lg font-medium transition-colors duration-200 hover:opacity-90"
                      style={{ backgroundColor: '#FFA07A' }}
                    >
                      {item.label}
                    </Link>
                  );
                }
                return (
                  <Link
                    key={item.key}
                    to={item.path}
                    className="text-gray-700 hover:text-blue-600 px-3 py-2 2xl:px-6 2xl:py-4 text-sm 2xl:text-lg font-medium transition-colors duration-200"
                  >
                    {item.label}
                  </Link>
                );
              })}
              
              {/* Download Button */}
              <Link
                to="/whitepaper"
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 2xl:px-8 2xl:py-4 rounded-full text-sm 2xl:text-lg font-medium transition-colors duration-200"
              >
                {t.download}
              </Link>

              {/* Language Toggle */}
              <button
                onClick={() => setLang(lang === 'ja' ? 'en' : 'ja')}
                className="p-2 2xl:p-4 text-sm 2xl:text-lg text-blue-600 hover:bg-blue-100 rounded-md flex items-center transition-colors"
                title={lang === 'ja' ? "Switch to English" : "日本語に切り替え"}
                aria-label={lang === 'ja' ? "Switch to English" : "日本語に切り替え"}
              >
                <Languages size={16} className="mr-1 2xl:w-6 2xl:h-6" /> {lang === 'ja' ? 'EN' : 'JA'}
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center justify-between w-full">
              {/* Mobile Hamburger - Left side */}
              <button
                onClick={toggleMenu}
                className="inline-flex items-center justify-center p-2 2xl:p-4 rounded-md text-gray-700 hover:text-blue-600 hover:bg-gray-100 focus:outline-none transition-colors duration-200"
                aria-expanded={isMenuOpen}
                aria-label={t.menuToggle}
              >
                {isMenuOpen ? <X size={24} className="2xl:w-8 2xl:h-8" /> : <Menu size={24} className="2xl:w-8 2xl:h-8" />}
              </button>
              
              {/* Mobile Language Toggle - Right side */}
              <button
                onClick={() => setLang(lang === 'ja' ? 'en' : 'ja')}
                className="p-2 2xl:p-4 text-sm 2xl:text-lg text-blue-600 hover:bg-blue-100 rounded-md flex items-center transition-colors"
                title={lang === 'ja' ? "Switch to English" : "日本語に切り替え"}
                aria-label={lang === 'ja' ? "Switch to English" : "日本語に切り替え"}
              >
                <Languages size={16} className="2xl:w-6 2xl:h-6" /> <span className="ml-1 text-xs 2xl:text-base">{lang === 'ja' ? 'EN' : 'JA'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white shadow-lg border-t border-gray-200">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-4 2xl:px-8 2xl:pt-4 2xl:pb-6">
              {navItems.map((item) => {
                if (item.key === 'contact') {
                  return (
                    <Link
                      key={item.key}
                      to={item.path}
                      onClick={closeMenu}
                      className="block w-full text-center text-white px-4 py-2 2xl:px-8 2xl:py-4 rounded-full text-base 2xl:text-xl font-medium transition-colors duration-200 mt-4 2xl:mt-6 hover:opacity-90"
                      style={{ backgroundColor: '#FFA07A' }}
                    >
                      {item.label}
                    </Link>
                  );
                }
                return (
                  <Link
                    key={item.key}
                    to={item.path}
                    onClick={closeMenu}
                    className="block px-3 py-2 2xl:px-6 2xl:py-4 rounded-md text-base 2xl:text-xl font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors duration-200"
                  >
                    {item.label}
                  </Link>
                );
              })}
              
              {/* Mobile Download Button */}
              <Link
                to="/whitepaper"
                onClick={closeMenu}
                className="block w-full text-center bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 2xl:px-8 2xl:py-4 rounded-full text-base 2xl:text-xl font-medium transition-colors duration-200 mt-4 2xl:mt-6"
              >
                {t.download}
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Mobile menu overlay */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-40 md:hidden"
          onClick={closeMenu}
        />
      )}
    </>
  );
};

export default Navigation;